import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import { Box, Stack, Drawer, Tooltip, CardActionArea, Typography } from '@mui/material';

import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import useGorgiasSettings from '../../hooks/settings/useGorgiasSettings';
import { PATH_DASHBOARD } from '../../routes/paths';
import Logo from '../../components/shared/Logo';
import Scrollbar from '../../components/shared/Scrollbar';
import NavSection from '../../components/shared/NavSection';
import sidebarConfig from './SidebarConfig';
import AccountPopover from './AccountPopover';
import { adminSection } from './SidebarConfig';
import AccountSwitcher from '../../components/settings/multiAccount/AccountSwitcher';

const DRAWER_WIDTH = 260;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('xs')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12]
}));


IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <Tooltip title="Mini Menu">
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: 'text.primary',
          justifyContent: 'center',
          border: 'solid 1px currentColor',
          ...(collapseClick && {
            borderWidth: 2
          })
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            transition: (theme) => theme.transitions.create('all'),
            ...(collapseClick && {
              width: 0,
              height: 0
            })
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ onCloseSidebar, isAdmin }) {
  const { pathname } = useLocation();
  const isOpenSidebar = true;
  const { 
    isCollapse, 
    collapseClick, 
    collapseHover, 
    onToggleCollapse, 
    onHoverEnter, 
    onHoverLeave 
  } = useCollapseDrawer();
  const {
    isAuthenticated: isGorgiasAuthenticated,
    loading: gorgiasLoading,
  } = useGorgiasSettings();
  const usedSidebarConfig = sidebarConfig.map((section) => ({
    ...section,
    items: section.items.filter((item) => (
      ![
        PATH_DASHBOARD.general.playground,
        PATH_DASHBOARD.general.tickets,
        PATH_DASHBOARD.general.stats,
        // Temporarily hide Custom Flows until the Gorgias integration supports them.
        PATH_DASHBOARD.general.settingsFlows,
      ].includes(item.path)
      || (!gorgiasLoading && !isGorgiasAuthenticated)
    )),
  }));
  if (isAdmin) {
    usedSidebarConfig.push(adminSection);
  }
  const email = localStorage.getItem('email');

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center'
          })
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            
            <AccountSwitcher />
          </Box>
        </Stack>
      </Stack>

      <NavSection navConfig={usedSidebarConfig} isShow={!isCollapse} />

      <Box sx={{ textAlign: 'center', mt: 'auto', mb: 4 }}>
        <AccountPopover />
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, fontSize: '11px' }}>{email}</Typography>
      </Box>
      
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: isOpenSidebar ? DRAWER_WIDTH : 0
      }}
    >
      <Drawer
        open={isOpenSidebar}
        variant="persistent"
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
        PaperProps={{
          sx: {
            width: isOpenSidebar ? DRAWER_WIDTH : 0,
            bgcolor: 'background.default',
            ...(isCollapse && {
              width: COLLAPSE_WIDTH
            }),
            ...(collapseHover && {
              borderRight: 0,
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
              boxShadow: (theme) => theme.customShadows.z20,
              bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
            })
          }
        }}
      >
        {renderContent}
      </Drawer>
    </RootStyle>
  );
}
